/**
 * Community Garden — Client-Side Router
 *
 * Handles routing between landing page, server select, and garden view.
 */

import { authService } from "./auth.js";
import { GardenWSClient } from "./ws-client.js";
import type { PetData, RankProgress } from "./ws-client.js";
import type {
  ActivityEntry,
  ProfileData,
  SocialUIManager,
  TopGardener,
} from "./social-ui-manager.js";
import { showActivityToast, showLiveActivityToast, destroyToastContainer } from "./toasts.js";
import type { AvatarEquipped } from "./avatar-catalog.js";
// GARDEN-100: Screenshot button moved to drawer; removeScreenshotButton kept for cleanup
import { removeScreenshotButton } from "./components/screenshot-button.js";
import { destroyOnboarding, initOnboarding } from "./onboarding.js";
// RISK-009: Memory leak prevention - cleanup verification
import { verifyNoMemoryLeaks, reportMemoryStatus } from "./components/cleanup-verification.js";
// GARDEN-137: Analytics tracking
import { analytics } from "./analytics.js";
// Guild-scoped points cache context
import { pointsStore } from "./points-state.js";
import { playerProgressionStore } from "./player-progression-store.js";
// GARDEN-165: Connection state machine
import { connectionStatus } from "./components/connection-status.js";
// 404 Not Found page
import { NotFoundPage } from "./pages/not-found.js";
import {
  calculateGardenerRankProgress,
  getGardenerRankByPoints,
  getNextGardenerRank,
} from "./progression/catalog.js";

export type Route =
  | { type: 'landing' }
  | { type: 'servers' }
  | { type: 'garden', guildId: string, panel?: string }
  | { type: 'not-found', path?: string };

type GardenRenderer = import('./renderer.js').GardenRenderer;
type GardenShell = import('./components/garden-shell.js').GardenShell;

export class Router {
  private currentRoute: Route | null = null;
  private renderer: GardenRenderer | null = null;
  private client: GardenWSClient | null = null;
  private serverSelect: import('./server-select.js').ServerSelect | null = null;
  private appEl: HTMLElement;
  private connEl: HTMLElement;
  private reconnectBtn: HTMLButtonElement | null = null;
  private loginUI: any = null;
  private gardenShell: GardenShell | null = null;
  private socialUIManager: SocialUIManager | null = null;
  private socialUIManagerPromise: Promise<SocialUIManager> | null = null;
  private busy = false;
  private queue: (() => Promise<void>)[] = [];
  /** Discord user ID of the authenticated user (set on init) */
  private currentDiscordId: string | null = null;
  /** Guard to prevent concurrent route renders (mobile refresh loop fix) */
  private isRendering = false;
  /** Pending route to render after current render completes */
  private pendingRoute: Route | null = null;
  /** Track if router has been started to prevent premature auth-triggered renders */
  private hasStarted = false;
  /** Avoid repeating cookie-session probes on every route during one page load. */
  private hasValidatedSession = false;
  /** Cleanup callbacks registered while garden route is active */
  private gardenCleanupFns: Array<() => void> = [];
  /** GARDEN-210: Store bound handlers and unsubscribe functions for cleanup */
  private boundHashChangeHandler: (() => void) | null = null;
  private boundPopStateHandler: (() => void) | null = null;
  private unsubscribeAuth: (() => void) | null = null;
  private unsubscribeConnectionStatus: (() => void) | null = null;
  /** GARDEN-061: Reconnect timer for manual reconnect button */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Not Found page instance */
  private notFoundPage: NotFoundPage | null = null;

  constructor(appEl: HTMLElement, connEl: HTMLElement) {
    this.appEl = appEl;
    this.connEl = connEl;

    // GARDEN-210: Store bound handlers for proper cleanup
    this.boundHashChangeHandler = () => this.handleRouteChange();
    this.boundPopStateHandler = () => this.handleRouteChange();

    // Listen for URL changes
    window.addEventListener('hashchange', this.boundHashChangeHandler);
    window.addEventListener('popstate', this.boundPopStateHandler);

    // Listen for auth changes to redirect if needed
    this.unsubscribeAuth = authService.subscribe(() => this.handleAuthChange());

    // GARDEN-165: Subscribe to connection state changes
    this.unsubscribeConnectionStatus = connectionStatus.subscribe((state, previousState) => {
      this.updateConnectionDisplay();
    });
  }

  /** GARDEN-165: Update connection display based on state machine */
  private updateConnectionDisplay(): void {
    const statusText = document.createElement('span');
    statusText.className = 'status-text';
    statusText.textContent = connectionStatus.getDisplayText();
    this.connEl.replaceChildren(statusText);
    this.connEl.setAttribute('aria-label', `Garden connection: ${statusText.textContent}`);
    this.connEl.className = connectionStatus.getCssClass();
    if (connectionStatus.shouldShowReconnectButton()) {
      this.showReconnectButton();
    } else {
      this.hideReconnectButton();
    }
  }

  /** Sync all known pet states into renderer so companions are visible to everyone. */
  private syncRendererPetsFromStates(petStates: Record<string, PetData> | undefined): void {
    if (!this.renderer || !petStates) return;
    for (const [userId, petData] of Object.entries(petStates)) {
      this.renderer.syncUserPet(userId, petData?.petType, petData?.petVariant, petData?.petStatus);
    }
  }

  private handleRouteChange() {
    // Skip route changes until router has started (prevents premature render during construction)
    if (!this.hasStarted) {
      return;
    }
    const route = this.parseRoute(window.location.hash);
    this.navigateTo(route, false); // false = don't push to history
  }

  private handleAuthChange() {
    // Skip auth changes until router has started (prevents premature render during construction)
    if (!this.hasStarted) {
      return;
    }
    // GARDEN-163: Skip auth changes while rendering to prevent race conditions
    if (this.isRendering) {
      return;
    }
    // If user logs in/out, we might need to redirect
    const route = this.parseRoute(window.location.hash);
    this.navigateTo(route, false);
  }

  private parseRoute(hash: string): Route {
    // Remove # and split
    const path = hash.replace(/^#/, '');
    const parts = path.split('/').filter(p => p);

    if (parts.length === 0) {
      return { type: 'landing' };
    }

    if (parts[0] === 'servers') {
      return { type: 'servers' };
    }

    if (parts[0] === 'garden' && parts[1]) {
      // SCOUT-075: Support panel sub-routes like #/garden/:guildId/quests
      // SCOUT-060: Normalize panel segment so hash query/trailing suffixes don't break routing
      const rawPanel = parts[2] || undefined;
      const panel = rawPanel
        ? decodeURIComponent(rawPanel).split('?')[0]?.trim().toLowerCase()
        : undefined;
      return { type: 'garden', guildId: parts[1], panel };
    }

    // Unknown route - return 404
    return { type: 'not-found', path: path || undefined };
  }

  public navigateTo(route: Route, pushToHistory = true) {
    // Don't re-render if we're already on this route
    if (JSON.stringify(this.currentRoute) === JSON.stringify(route)) {
      return;
    }

    // Update URL if needed
    if (pushToHistory) {
      const hash = this.routeToHash(route);
      window.location.hash = hash;
    }

    // GARDEN-137: Track page view
    const pagePath = this.routeToHash(route);
    const pageTitle = this.getRouteTitle(route);
    analytics.trackPageView(pagePath, pageTitle);

    // If already rendering, queue this route for after current render completes
    if (this.isRendering) {
      this.pendingRoute = route;
      return;
    }

    // GARDEN-164: Set currentRoute ONLY when we're actually going to render
    // Moving this after the isRendering check fixes the race condition where
    // pending routes would see currentRoute already set and return early
    this.currentRoute = route;

    // Render the route
    this.renderRoute(route);
  }

  /**
   * GARDEN-137: Get page title for analytics
   */
  private getRouteTitle(route: Route): string {
    switch (route.type) {
      case 'landing':
        return 'Community Garden - Home';
      case 'servers':
        return 'Community Garden - Server Select';
      case 'garden':
        return `Community Garden - Garden (${route.guildId})`;
      default:
        return 'Community Garden';
    }
  }

  private routeToHash(route: Route): string {
    switch (route.type) {
      case 'landing':
        return '#/';
      case 'servers':
        return '#/servers';
      case 'garden':
        return `#/garden/${route.guildId}`;
      default:
        return '#/';
    }
  }

  private async renderRoute(route: Route) {
    // Guard against concurrent renders (mobile refresh loop fix)
    if (this.isRendering) {
      this.pendingRoute = route;
      return;
    }
    this.isRendering = true;

    try {
      await this._doRenderRoute(route);
    } catch (error) {
      console.error('[Router] Failed to render route:', error);
      this.renderRouteError(route);
    } finally {
      this.isRendering = false;
      // Process any pending route that was requested during render
      if (this.pendingRoute) {
        const nextRoute = this.pendingRoute;
        this.pendingRoute = null;
        // Use setTimeout to break the call stack and allow cleanup
        setTimeout(() => this.navigateTo(nextRoute, false), 0);
      }
    }
  }

  private renderRouteError(route: Route): void {
    document.documentElement.classList.remove('garden-view');
    document.body.classList.remove('garden-view');
    this.appEl.classList.remove('garden-view');
    this.connEl.style.display = 'none';

    const hud = document.getElementById('hud');
    if (hud) {
      hud.style.visibility = 'hidden';
      hud.style.pointerEvents = 'none';
    }
    const statusCluster = document.getElementById('status-cluster');
    if (statusCluster) {
      statusCluster.style.visibility = 'hidden';
    }

    Array.from(this.appEl.children).forEach((child) => {
      const el = child as HTMLElement;
      if (el.id !== 'hud' && el.id !== 'status-cluster' && el.id !== 'offline-indicator') {
        el.remove();
      }
    });

    const fallback = document.createElement('main');
    fallback.className = 'route-error-state';
    fallback.setAttribute('role', 'alert');

    const title = document.createElement('h1');
    title.textContent = 'This page did not load';
    const message = document.createElement('p');
    message.textContent = 'Your garden is still safe. Please try loading the page again.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'btn-primary';
    retry.textContent = 'Try Again';
    retry.addEventListener('click', () => {
      this.currentRoute = null;
      this.navigateTo(route, false);
    });

    fallback.append(title, message, retry);
    this.appEl.appendChild(fallback);
  }

  private async _doRenderRoute(route: Route) {
    // Clear route-mounted content from #app, preserving static HUD/status nodes.
    Array.from(this.appEl.children).forEach((child) => {
      const el = child as HTMLElement;
      if (el.id === 'hud' || el.id === 'status-cluster' || el.id === 'offline-indicator') {
        return;
      }
      el.remove();
    });

    // Clean up garden-route scoped listeners/subscriptions
    this.cleanupGardenRouteResources();
    this.gardenShell?.destroy();
    this.gardenShell = null;

    // Clean up previous renderer and client
    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }

    if (this.client) {
      this.client.destroy();
      this.client = null;
    }

    if (this.loginUI) {
      this.loginUI.destroy();
      this.loginUI = null;
    }

    if (this.serverSelect) {
      this.serverSelect.destroy();
      this.serverSelect = null;
    }

    this.socialUIManager?.destroy();

    // GARDEN-052: Clean up toast container
    destroyToastContainer();

    // GARDEN-061: Clean up reconnect button
    this.hideReconnectButton();

    // GARDEN-067: Clean up screenshot button
    removeScreenshotButton();

    // GARDEN-068: Clean up onboarding if active
    destroyOnboarding();

    // GARDEN-165: Reset connection state machine when leaving garden
    connectionStatus.reset();

    // RISK-009: Verify no memory leaks after cleanup
    if (!verifyNoMemoryLeaks()) {
      console.warn('[Router] Potential memory leak detected after cleanup');
      reportMemoryStatus();
    }

    // Reset queue
    this.busy = false;
    this.queue = [];

    if (route.type !== 'garden') {
      this.currentDiscordId = null;
      playerProgressionStore.clear();
      pointsStore.setGuildContext(null);
    }

    // Show/hide UI elements based on route
    const hudEl = document.getElementById('hud');
    const appEl = document.getElementById('app');
    const htmlEl = document.documentElement;
    this.placeHudOverlaysForRoute(route.type === 'garden');
    if (route.type === 'garden') {
      // Show HUD/status elements immediately in garden view.
      // Visibility is controlled by CSS; avoid inline opacity gating.
      this.connEl.style.display = 'block';
      this.connEl.style.transition = '';
      if (hudEl) {
        hudEl.style.display = 'block';
        hudEl.style.visibility = 'visible';
        hudEl.style.pointerEvents = '';
        hudEl.style.transition = '';
      }
      const statusCluster = document.getElementById('status-cluster');
      if (statusCluster) {
        statusCluster.style.display = 'flex';
        statusCluster.style.visibility = 'visible';
        statusCluster.style.transition = '';
      }
      // Lock page scrolling via CSS classes for garden view
      htmlEl.classList.add('garden-view');
      document.body.classList.add('garden-view');
      if (appEl) {
        appEl.classList.add('garden-view');
      }
    } else {
      this.connEl.style.display = 'none';
      if (hudEl) {
        hudEl.style.visibility = 'hidden';
        hudEl.style.pointerEvents = 'none';
      }
      const statusCluster = document.getElementById('status-cluster');
      if (statusCluster) {
        statusCluster.style.visibility = 'hidden';
      }
      // Restore default page scrolling for landing page and server select
      htmlEl.classList.remove('garden-view');
      document.body.classList.remove('garden-view');
      if (appEl) {
        appEl.classList.remove('garden-view');
      }
    }

    // Render based on route type
    switch (route.type) {
      case 'landing':
        await this.renderLandingPage();
        break;
      case 'servers':
        await this.renderServerSelect();
        break;
      case 'garden':
        await this.renderGarden(route.guildId);
        // SCOUT-071: On SPA navigation (/servers -> /garden/:id), ensure HUD controls
        // are mounted after route transition. If an auth/state race removed the social
        // toggle during transition, re-initialize social UI once.
        await this.ensureGardenHudMounted();
        break;
    }
  } // End of _doRenderRoute

  private async loadSocialUIManager(): Promise<SocialUIManager> {
    if (this.socialUIManager) return this.socialUIManager;
    if (this.socialUIManagerPromise) return this.socialUIManagerPromise;

    this.socialUIManagerPromise = import('./social-ui-manager.js')
      .then(({ socialUIManager }) => {
        this.socialUIManager = socialUIManager;
        return socialUIManager;
      })
      .catch((error) => {
        this.socialUIManagerPromise = null;
        throw error;
      });

    return this.socialUIManagerPromise;
  }

  private async renderLandingPage() {
    // Landing page now handles authenticated users (shows "Your Gardens" CTA
    // instead of "Login with Discord"), so we no longer force-redirect. This
    // lets users browse the feature page and share the URL even while logged in.
    const { LandingPage } = await import('./landing-page.js');
    const landingContainer = document.createElement('div');
    landingContainer.id = 'landing-page-root';
    this.appEl.appendChild(landingContainer);
    const landingPage = new LandingPage(landingContainer);
    landingPage.render();
  }

  private async renderServerSelect() {
    // Restore a cookie session when available, but keep public discovery open
    // to logged-out visitors. The API returns only explicitly public gardens.
    if (!authService.isAuthenticated() && !this.hasValidatedSession) {
      await authService.validateSession();
      this.hasValidatedSession = true;
    }

    // Import and render server select — use a sub-container to avoid innerHTML
    // wiping static HUD elements that share #app as ancestor.
    const serverContainer = document.createElement('div');
    serverContainer.id = 'server-select-root';
    this.appEl.appendChild(serverContainer);
    const { ServerSelect } = await import('./server-select.js');
    this.serverSelect = new ServerSelect(serverContainer, this);
    await this.serverSelect.render();
  }

  private async renderGarden(guildId: string) {
    // Check if user is logged in (and attempt session restore on refresh)
    let isAuthed = authService.isAuthenticated();
    if (!isAuthed && !this.hasValidatedSession) {
      isAuthed = await authService.validateSession();
      this.hasValidatedSession = true;
    }

    // Anonymous garden viewing is a core product promise ("anyone with the
    // link sees the living garden"). Unauthenticated visitors get a view-only
    // garden: ws-client identifies as type "viewer" when no token is present,
    // and the server accepts viewer connections (SCOUT-004). Auth-gated UI
    // (profile push, social panels) already no-ops without a session, so we
    // no longer redirect logged-out visitors to the landing page here.

    // GARDEN-137: Set analytics context
    analytics.setGuild(guildId);

    // Points are guild-scoped: tell the store which guild we're in so the
    // localStorage fallback can't leak another guild's cached value.
    pointsStore.setGuildContext(guildId);

    if (isAuthed) {
      const authState = authService.getState();
      if (authState.user?.id) {
        analytics.setUser(authState.user.id);
        // Garden state is keyed by Discord ID, not the internal account UUID.
        this.currentDiscordId = authState.user.discord_id;
      }

      // GARDEN-197: Check if user has access to this guild before rendering.
      // Only meaningful for authenticated users — anonymous visitors are
      // view-only and access is enforced server-side by the WebSocket.
      const hasAccess = await this.checkGuildAccess(guildId);
      if (!hasAccess) {
        console.warn(`[Router] User does not have access to guild ${guildId}, redirecting to server select`);
        this.navigateTo({ type: 'servers' });
        return;
      }
    }

    playerProgressionStore.setContext(guildId, this.currentDiscordId);

    // Create garden container
    const gardenContainer = document.createElement('div');
    gardenContainer.id = 'garden-container';
    gardenContainer.className = 'garden-route-surface';
    this.appEl.appendChild(gardenContainer);

    let socialUIManager: SocialUIManager | null = null;
    if (isAuthed) {
      try {
        socialUIManager = await this.loadSocialUIManager();
      } catch (error) {
        console.error('[Router] Social UI module failed to load:', error);
      }
    }

    // Lazy-load PIXI renderer only when entering garden route
    const { GardenRenderer } = await import('./renderer.js');
    this.renderer = new GardenRenderer(gardenContainer, socialUIManager);
    this.renderer.setStageCelebrationContext(guildId);

    // Expose renderer for testing (GARDEN-111)
    (window as any).gardenRenderer = this.renderer;

    // GARDEN-100-FIX: Wire up drawer button event listeners
    const onGardenScreenshot = () => {
      analytics.trackFeatureUsage('screenshot', guildId);
      this.renderer?.triggerScreenshot();
    };
    const onGardenFocusMe = () => {
      analytics.trackFeatureUsage('focus_me', guildId);
      this.renderer?.focusOnMe();
    };
    window.addEventListener('garden-screenshot', onGardenScreenshot);
    window.addEventListener('garden-focus-me', onGardenFocusMe);
    this.gardenCleanupFns.push(() => {
      window.removeEventListener('garden-screenshot', onGardenScreenshot);
      window.removeEventListener('garden-focus-me', onGardenFocusMe);
    });

    // Anonymous visitors get the garden shell and a clear sign-in path, but not
    // a cluster of disabled profile/social controls.
    if (socialUIManager) {
      try {
        await socialUIManager.initialize();
      } catch (err) {
        console.error('[Router] Social UI init failed:', err);
      }
    }

    // GARDEN-084: Wire up pet feed animation callback
    if (socialUIManager) {
      socialUIManager.onPetFed = (userId) => {
        this.renderer?.playPetFeedAnimation(userId);
      };
      socialUIManager.onPetChanged = (userId, petType, petVariant, petStatus) => {
        this.renderer?.syncUserPet(userId, petType, petVariant, petStatus);
      };
    }

    // Initialize login UI
    const { LoginUI } = await import('./login-ui.js');
    this.loginUI = new LoginUI();

    const { GardenShell } = await import('./components/garden-shell.js');
    this.gardenShell = new GardenShell({
      authenticated: isAuthed,
      communityAvailable: !!socialUIManager,
      onFocusGarden: () => this.renderer?.focusOnCenter(),
      onOpenCommunity: () => {
        socialUIManager?.openCommunityPanel();
      },
      onOpenGuide: () => {
        socialUIManager?.openFaqPanel();
      },
      onCloseCommunity: () => {
        socialUIManager?.closePanel();
      },
      onLogin: () => {
        authService.clearError();
        void authService.loginWithDiscord().catch((error) => {
          console.error('[Router] Discord login failed:', error);
        });
      },
    });
    this.gardenShell.mount();
    this.gardenCleanupFns.push(() => {
      this.gardenShell?.destroy();
      this.gardenShell = null;
    });

    // Initialize WebSocket client
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const WS_URL = `${wsProtocol}//${window.location.host}/ws`;

    this.client = new GardenWSClient(WS_URL, {
      onConnect: () => connectionStatus.connected(),
      onDisconnect: () => connectionStatus.disconnected(),
      onReconnecting: () => connectionStatus.connecting(),
      onFailed: () => connectionStatus.failed('Connection lost. Click to reconnect.'),
      onInit: async (data: any) => {
        // GARDEN-165: Set refreshing state while loading data
        connectionStatus.refreshing();
        if (data?.user?.discord_id) this.currentDiscordId = data.user.discord_id;
        const viewerRankProgress = isAuthed ? this.resolveRankProgress(data) : null;
        data.rankProgress = viewerRankProgress;
        playerProgressionStore.setContext(guildId, this.currentDiscordId);
        if (viewerRankProgress) playerProgressionStore.setRankProgress(viewerRankProgress);
        const stageUserId = data?.user?.discord_id ?? this.currentDiscordId ?? null;
        this.renderer?.setStageCelebrationContext(guildId, stageUserId);

        // GARDEN-153: Check if this is a server-confirmed reconnect
        const isReconnect = data.serverReconnect === true;
        if (isReconnect && import.meta.env.DEV) {
          console.log('[Router] Server confirmed reconnect - using fresh state, no stale data');
        }

        // GARDEN-135: Sync pet states on reconnect
        if (isAuthed && data.petStates) {
          socialUIManager?.syncPetStatesOnReconnect(data.petStates);
        }

        // GARDEN-143: Apply URL theme override if present (preview only, not persisted)
        const urlParams = new URLSearchParams(window.location.search);
        const themeOverride = urlParams.get('theme');
        if (themeOverride && (themeOverride === 'garden' || themeOverride === 'castle')) {
          data.themeId = themeOverride;
        }

        // Reflect authoritative progress before the heavier world hydration so
        // the HUD never presents default Stage 0 while the scene is being built.
        this.gardenShell?.update({
          totalPoints: data.totalPoints ?? 0,
          stage: data.stage ?? 0,
          gardeners: Object.keys(data.users ?? {}).length,
          stageProgress: data.stageProgress ?? null,
          rankProgress: viewerRankProgress,
          communityWork: data.communityWork ?? null,
        });

        // The WebSocket snapshot is the shared garden authority. A historical
        // per-user tile snapshot must never override community points or stage.
        this.renderer!.initGarden(data);
        if (authService.isAuthenticated()) {
          // Push profile data to the drawer's Profile panel
          // GARDEN-153: On reconnect, this refreshes avatar, points, rank from server
          this.pushProfileData(data);
          // Tell renderer who "me" is so Focus Me + Minimap can work
          if (this.currentDiscordId) {
            this.renderer!.setMyUserId(this.currentDiscordId);
            this.renderer!.setStageCelebrationContext(guildId, this.currentDiscordId);
          }
        }

        // Ensure pets render for all users, not only the local user.
        this.syncRendererPetsFromStates(data.petStates);

        // Play opening settle animation & fade in UI overlays
        this.playOpeningAnimation();

        // GARDEN-068: Start onboarding for first-time users
        if (isAuthed) initOnboarding();

        // Init payload has been fully applied, reflect ready state in HUD.
        connectionStatus.connected();

        // SCOUT-075: Open specific panel based on URL route
        const route = this.currentRoute;
        if (route?.type === 'garden' && route.panel) {
          setTimeout(() => {
            switch (route.panel) {
              case 'quests':
                socialUIManager?.openPanel();
                socialUIManager?.openQuestPanel();
                break;
              case 'leaderboard':
                socialUIManager?.openPanel();
                socialUIManager?.expandLeaderboard();
                break;
              case 'avatar':
              case 'avatar-customize':
              case 'avatar customize':
                socialUIManager?.openPanel();
                socialUIManager?.openCustomizePanel();
                break;
              case 'guide':
              case 'garden-guide':
              case 'garden guide':
                socialUIManager?.openPanel();
                socialUIManager?.openFaqPanel();
                break;
              case 'progress':
              case 'rank':
                document.querySelector<HTMLButtonElement>('[data-open-garden-progress]')?.click();
                break;
            }
          }, 500);
        }
      },
      onActivity: (data: any) => {
        const isCurrentUserEvent = !!this.currentDiscordId && data.userId === this.currentDiscordId;
        const personalRankProgress = isCurrentUserEvent ? data.rankProgress : undefined;
        this.queue.push(async () => {
          await this.renderer!.playActivity({ ...data, rankProgress: personalRankProgress });
        });
        // Update profile panel if this activity is for the current user
        if (authService.isAuthenticated() && this.currentDiscordId) {
          if (isCurrentUserEvent) {
            socialUIManager?.setLoggedInUserRankTier(data.rankTier ?? null);
            socialUIManager?.setProfileData({
              userId: data.userId,
              username: data.userName,
              isBot: data.userIsBot ?? false,
              userGardenPoints: data.userPoints ?? 0,
              rankTitle: data.rankTitle ?? '—',
              rankTier: data.rankTier ?? '—',
              rankColor: data.rankColor ?? '#78909C',
              gardenStage: data.stage ?? 0,
            });
            if (data.rankProgress) {
              playerProgressionStore.setRankProgress(data.rankProgress);
              socialUIManager?.setRankProgress(data.rankProgress);
            }
          }
          // Update top gardeners leaderboard
          if (data.topGardeners) {
            socialUIManager?.setTopGardeners(data.topGardeners as TopGardener[]);
          }
          // Update progress bars
          if (data.stageProgress) {
            socialUIManager?.setStageProgress(data.stageProgress);
          }
          // Refresh recent activity when the current user earns points
          if (isCurrentUserEvent && this.currentRoute?.type === 'garden') {
            this.fetchRecentActivity(this.currentRoute.guildId);
          }
        }
        if (authService.isAuthenticated()) {
          socialUIManager?.pushServerRecentActivity({
            action_type: data.replyToId ? 'reply' : 'message',
            points_awarded: Number(data.pointsDelta ?? 0),
            multiplier_note: null,
            created_at: new Date().toISOString(),
            username: data.userName ?? data.username ?? 'Member',
          });
        }
        showLiveActivityToast(data.userName, data.actionType, data.replyToName, data.pointsDelta);
        this.gardenShell?.update({
          totalPoints: data.totalPoints,
          stage: data.stage,
          stageProgress: data.stageProgress ?? null,
          communityWork: data.communityWork ?? null,
          ...(isCurrentUserEvent && data.rankProgress ? { rankProgress: data.rankProgress } : {}),
        });
        this.processQueue();
      },
      onScore: (data: any) => {
        const isCurrentUserEvent = !!this.currentDiscordId && data.userId === this.currentDiscordId;
        const personalRankProgress = isCurrentUserEvent ? data.rankProgress : undefined;
        this.renderer!.updateUserProgress(
          data.userId,
          data.userPoints,
          data.rankTier,
          data.rankColor,
          data.rankTitle,
        );
        this.renderer!.updateScore(
          data.totalPoints,
          data.stage,
          data.stageProgress,
          personalRankProgress,
          data.tiles,
          data.communityWork,
        );
        this.gardenShell?.update({
          totalPoints: data.totalPoints,
          stage: data.stage,
          stageProgress: data.stageProgress ?? null,
          communityWork: data.communityWork ?? null,
          ...(isCurrentUserEvent && data.rankProgress ? { rankProgress: data.rankProgress } : {}),
        });
        if (authService.isAuthenticated()) {
          if (isCurrentUserEvent && data.userPoints !== undefined) {
            socialUIManager?.updateUserPoints(data.userPoints);
          }
          if (isCurrentUserEvent && data.rankProgress) {
            playerProgressionStore.setRankProgress(data.rankProgress);
            socialUIManager?.setRankProgress(data.rankProgress);
          }
          // Update top gardeners leaderboard on score changes
          if (data.topGardeners) {
            socialUIManager?.setTopGardeners(data.topGardeners as TopGardener[]);
          }
          // Update progress bars
          if (data.stageProgress) {
            socialUIManager?.setStageProgress(data.stageProgress);
          }
        }
        if (authService.isAuthenticated()) {
          socialUIManager?.pushServerRecentActivity({
            action_type: data.source?.startsWith('quest') ? 'quest' : 'reaction',
            points_awarded: Number(data.pointsDelta ?? 0),
            multiplier_note: null,
            created_at: new Date().toISOString(),
            username: data.userName ?? data.username ?? 'Member',
          });
        }
      },
      onNotification: (data) => {
        socialUIManager?.addNotification(data.notification);
      },
      onAccountPoints: (data) => {
        authService.updateAccountPoints(data.accountPoints);
      },
      // GARDEN-047: Handle live theme changes
      onThemeChanged: (data: any) => {
        if (data.themeId && this.renderer) {
          const previousTheme = this.renderer.theme?.id;
          this.renderer.setTheme(data.themeId);
          // GARDEN-137: Track theme change
          analytics.trackThemeChange(data.themeId, previousTheme, guildId);
        }
      },
      // GARDEN-046: Handle avatar updates from other users (or self via WS echo)
      onAvatarUpdated: (data: any) => {
        // If it's the current user's avatar, update the profile drawer
        if (data.userId === this.currentDiscordId) {
          socialUIManager?.setAvatarData(data.avatar as AvatarEquipped);
        }
        // Update the in-world renderer (avatar sprite colours)
        this.renderer?.updateUserAvatar(data.userId, data.avatar);
      },
      // GARDEN-135: Handle pet fed events from WebSocket
      onPetFed: (data: any) => {
        this.renderer?.syncUserPet(data.userId, data.petData?.petType, data.petData?.petVariant, data.petData?.petStatus);
        socialUIManager?.handlePetFedFromWS(data.userId, data.petData, data.streak);
      },
      // GARDEN-135: Handle pet state updates from WebSocket
      onPetStateUpdate: (data: any) => {
        this.renderer?.syncUserPet(data.userId, data.petData?.petType, data.petData?.petVariant, data.petData?.petStatus);
        socialUIManager?.handlePetStateUpdateFromWS(data.userId, data.petData, data.oldStatus, data.newStatus);
      },
      // GARDEN-135: Handle pet adopted events from WebSocket
      onPetAdopted: (data: any) => {
        // Refresh pet data from server to get updated state
        if (data.userId === this.currentDiscordId) {
          this.fetchAndUpdatePetData();
        }
      },
      // GARDEN-135: Handle pet wandered events from WebSocket
      onPetWandered: (data: any) => {
        socialUIManager?.handlePetStateUpdateFromWS(data.userId, {
          totalLoginDays: 0,
          lastLoginDayKey: null,
          petUnlocked: true,
          petType: null,
          petVariant: null,
          petStatus: 'gone',
          petFedDayKey: null,
          lastFedDayKey: null,
          petFeedStreak: 0,
        }, 'active', 'gone');
        // GARDEN-137: Track pet wander event
        if (data.userId === this.currentDiscordId) {
          analytics.trackPetInteraction('wander');
        }
      },
    }, guildId);

    this.client.connect();

    // Listen for auth state changes
    let socialInitTimer: number | null = null;
    const unsubscribeAuth = authService.subscribe((authState) => {
      if (socialInitTimer !== null) {
        window.clearTimeout(socialInitTimer);
        socialInitTimer = null;
      }
      if (authState.user) {
        socialInitTimer = window.setTimeout(() => {
          socialInitTimer = null;
          if (this.currentRoute?.type !== 'garden') return;
          void this.loadSocialUIManager()
            .then((manager) => {
              if (this.currentRoute?.type !== 'garden') return;
              this.renderer?.setUIBridge(manager);
              manager.onPetFed = (userId) => this.renderer?.playPetFeedAnimation(userId);
              manager.onPetChanged = (userId, petType, petVariant, petStatus) => {
                this.renderer?.syncUserPet(userId, petType, petVariant, petStatus);
              };
              return manager.initialize();
            })
            .catch((error) => {
              console.warn('Failed to initialize social UI:', error);
            });
        }, 1000);
      } else {
        this.socialUIManager?.destroy();
        this.renderer?.setUIBridge(null);
      }
    });
    this.gardenCleanupFns.push(() => {
      unsubscribeAuth();
      if (socialInitTimer !== null) window.clearTimeout(socialInitTimer);
    });

    // GARDEN-100: Screenshot button moved to garden controls drawer
    // The screenshot functionality is now accessed via the ☰ menu (drawer)
    // initScreenshotButton call removed - screenshot button consolidated into drawer
  }

  /** Fetch recent activity for the current user in this guild */
  private async fetchRecentActivity(guildId: string): Promise<void> {
    if (!this.currentDiscordId) return;
    try {
      const headers = await authService.getAuthHeaders();
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const resp = await fetch(
        `${baseUrl}/api/garden/${encodeURIComponent(guildId)}/user/${encodeURIComponent(this.currentDiscordId)}/activity?limit=5`,
        Object.keys(headers).length > 0 ? { headers } : undefined
      );
      if (!resp.ok) return;
      const json = await resp.json();
      if (json.activity) {
        this.socialUIManager?.setRecentActivity(json.activity as ActivityEntry[]);
      }
    } catch (e) {
      console.warn('[Router] Failed to fetch recent activity:', e);
    }
  }

  /** Fetch recent activity for everyone in this guild */
  private async fetchServerRecentActivity(guildId: string): Promise<void> {
    try {
      const headers = await authService.getAuthHeaders();
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const resp = await fetch(
        `${baseUrl}/api/garden/${encodeURIComponent(guildId)}/activity?limit=5`,
        Object.keys(headers).length > 0 ? { headers } : undefined
      );
      if (!resp.ok) return;
      const json = await resp.json();
      if (json.activity) {
        this.socialUIManager?.setServerRecentActivity(json.activity as ActivityEntry[]);
      }
    } catch (e) {
      console.warn('[Router] Failed to fetch server recent activity:', e);
    }
  }

  /** Extract profile data from init payload and push to social UI */
  private pushProfileData(data: any): void {
    if (!authService.isAuthenticated()) return;
    const socialUIManager = this.socialUIManager;
    if (!socialUIManager) return;
    const authState = authService.getState();
    const authUser = authState.user;
    if (!authUser) return;

    // data.user.discord_id is the Discord user ID; garden users map is keyed by this
    const discordId = (data.user?.discord_id ?? authUser.discord_id) as string;
    this.currentDiscordId = discordId;
    const gardenUser = data.users?.[discordId];
    const personalPoints = Number(gardenUser?.points ?? 0);
    const fallbackRank = getGardenerRankByPoints(personalPoints);

    // GARDEN-153: Use server-provided user data (refreshed on reconnect)
    const profile: ProfileData = {
      userId: discordId,
      username: data.user?.username ?? authUser.username ?? gardenUser?.name ?? '—',
      isBot: gardenUser?.isBot ?? false,
      userGardenPoints: personalPoints,
      rankTitle: gardenUser?.rankTitle ?? fallbackRank.title,
      rankTier: gardenUser?.rankTier ?? fallbackRank.tier,
      rankColor: gardenUser?.rankColor ?? fallbackRank.color,
      gardenStage: data.stage ?? 0,
    };

    socialUIManager.setLoggedInUserRankTier(profile.rankTier ?? null);
    socialUIManager.setProfileData(profile);

    // Keep character panel sprite in sync with in-world avatar on initial load/reload.
    if (gardenUser?.avatar) {
      socialUIManager.setAvatarData(gardenUser.avatar as AvatarEquipped);
    }

    // GARDEN-046: Set garden ID for avatar save endpoint
    if (this.currentRoute?.type === 'garden') {
      socialUIManager.setGardenId(this.currentRoute.guildId);
      // GARDEN-153: Fetch user's avatar (refreshed on reconnect)
      this.fetchUserAvatar(this.currentRoute.guildId);
    }

    // Push top gardeners leaderboard
    if (data.topGardeners) {
      socialUIManager.setTopGardeners(data.topGardeners as TopGardener[]);
    }

    // Push progress bar data (GARDEN-037)
    if (data.stageProgress) {
      socialUIManager.setStageProgress(data.stageProgress);
    }
    const rankProgress = this.resolveRankProgress(data);
    if (rankProgress) {
      playerProgressionStore.setContext(
        this.currentRoute?.type === 'garden' ? this.currentRoute.guildId : null,
        discordId,
      );
      playerProgressionStore.setRankProgress(rankProgress);
      socialUIManager.setRankProgress(rankProgress);
    }

    // Set pet data from init payload (initial connect and reconnect)
    if (data.petData) {
      socialUIManager.setPetData(data.petData);
    }

    // Fetch recent activity for this user in this garden
    if (this.currentRoute && this.currentRoute.type === 'garden') {
      this.fetchRecentActivity(this.currentRoute.guildId);
      this.fetchServerRecentActivity(this.currentRoute.guildId);
    }
  }

  /** Keep a truthful personal rank state even before a member earns activity in this garden. */
  private resolveRankProgress(data: any): RankProgress | null {
    if (data?.rankProgress) return data.rankProgress as RankProgress;
    if (!authService.isAuthenticated()) return null;

    const authUser = authService.getState().user;
    const discordId = data?.user?.discord_id ?? authUser?.discord_id;
    const personalPoints = Number(discordId ? data?.users?.[discordId]?.points ?? 0 : 0);
    const rank = getGardenerRankByPoints(personalPoints);
    const next = getNextGardenerRank(rank.tier);

    return {
      mode: 'threshold',
      userPoints: personalPoints,
      rankTier: rank.tier,
      rankTitle: rank.title,
      rankColor: rank.color,
      nextRankTitle: next?.title,
      nextRankThreshold: next?.threshold,
      prevRankThreshold: rank.threshold,
      progress: calculateGardenerRankProgress(personalPoints),
      isMax: !next,
    };
  }

  /** GARDEN-046: Fetch user's avatar from the server */
  private async fetchUserAvatar(gardenId: string): Promise<void> {
    try {
      const headers = await authService.getAuthHeaders();
      if (!headers['Authorization']) return;
      const resp = await fetch(
        `/api/gardens/${encodeURIComponent(gardenId)}/avatar`,
        { headers }
      );
      if (!resp.ok) return;
      const json = await resp.json();
      if (json.avatar) {
        this.socialUIManager?.setAvatarData(json.avatar as AvatarEquipped);
      }
    } catch (e) {
      console.warn('[Router] Failed to fetch avatar:', e);
    }
  }

  /**
   * GARDEN-197: Check if the current user has access to a specific guild.
   * Fetches the user's accessible gardens and verifies membership.
   */
  private async checkGuildAccess(guildId: string): Promise<boolean> {
    try {
      const headers = await authService.getAuthHeaders();
      if (!headers['Authorization']) {
        return false;
      }

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const resp = await fetch(
        `${baseUrl}/api/me/gardens`,
        { headers }
      );

      if (!resp.ok) {
        console.warn('[Router] Failed to fetch user gardens:', resp.status);
        // Fail open if we can't verify - the WebSocket will reject if truly unauthorized
        return true;
      }

      const json = await resp.json();
      const gardens = json.gardens || [];

      // Check if the requested guild is in the user's accessible gardens.
      // /api/me/gardens returns { gardens: [{ guild: { discord_id, ... }, ... }] } —
      // the guild ID is nested under g.guild, not at the top level. Checking only
      // top-level fields made this always return false, locking every user out of
      // every garden ("does not have access" redirect loop).
      return gardens.some(
        (g: any) =>
          g?.guild?.discord_id === guildId ||
          g?.guild?.id === guildId ||
          g?.discord_id === guildId ||
          g?.id === guildId
      );
    } catch (e) {
      console.warn('[Router] Error checking guild access:', e);
      // Fail open on error - let the WebSocket auth handle it
      return true;
    }
  }

  /** GARDEN-135: Fetch and update pet data from the server */
  private async fetchAndUpdatePetData(): Promise<void> {
    try {
      const headers = await authService.getAuthHeaders();
      if (!headers['Authorization']) return;
      const resp = await fetch(
        '/api/pet/state',
        { headers }
      );
      if (!resp.ok) return;
      const json = await resp.json();
      if (json.petData) {
        this.socialUIManager?.setPetData(json.petData);
      }
    } catch (e) {
      console.warn('[Router] Failed to fetch pet data:', e);
    }
  }

  /** Play the opening settle animation. */
  private playOpeningAnimation(): void {
    if (!this.renderer) return;

    // Keep the camera/particle settle animation, but don't gate HUD visibility.
    this.renderer.playEntryAnimation();
  }

  /** SCOUT-071: Ensure HUD/social controls are mounted after SPA garden navigation. */
  private async ensureGardenHudMounted(): Promise<void> {
    if (this.currentRoute?.type !== 'garden') return;

    const hudEl = document.getElementById('hud');
    const statusCluster = document.getElementById('status-cluster');
    // SCOUT-081: Don't show HUD if it was hidden by the social panel
    if (hudEl && !hudEl.dataset.hiddenByPanel) {
      hudEl.style.display = 'block';
      hudEl.style.visibility = 'visible';
      hudEl.style.pointerEvents = '';
    }
    if (statusCluster && !statusCluster.dataset.hiddenByPanel) {
      statusCluster.style.display = 'flex';
      statusCluster.style.visibility = 'visible';
    }

    // social-ui-toggle is created by SocialUIManager.initialize().
    if (authService.isAuthenticated() && !document.getElementById('social-ui-toggle')) {
      try {
        const socialUIManager = await this.loadSocialUIManager();
        socialUIManager.destroy();
        this.renderer?.setUIBridge(socialUIManager);
        await socialUIManager.initialize();
      } catch (error) {
        console.warn('[Router] Failed to recover social HUD:', error);
      }
    }
  }

  private async processQueue() {
    if (this.busy) return;
    while (this.queue.length > 0) {
      this.busy = true;
      const task = this.queue.shift()!;
      await task();
    }
    this.busy = false;
  }

  private cleanupGardenRouteResources(): void {
    for (const fn of this.gardenCleanupFns) {
      try {
        fn();
      } catch (error) {
        console.warn('[Router] Garden cleanup callback failed:', error);
      }
    }
    this.gardenCleanupFns = [];
  }

  /**
   * iOS Safari fix: fixed-position HUD overlays can disappear when nested inside
   * overflow-hidden containers. Move them to document.body in garden view.
   */
  private placeHudOverlaysForRoute(isGardenRoute: boolean): void {
    // SCOUT-083: Always keep overlays on body. They're position:fixed so parent
    // doesn't matter for layout. Moving to #app caused ServerSelect/LandingPage
    // innerHTML to destroy them.
    const overlayIds = ['hud', 'status-cluster', 'offline-indicator'];
    for (const id of overlayIds) {
      const el = document.getElementById(id);
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    }
  }

  public async start() {
    // GARDEN-160-FIX: Wait for auth service to complete session validation
    // before processing the initial route. This prevents race conditions
    // where the router sees unauthenticated state during OAuth callback.
    await authService.validateSession();
    this.hasValidatedSession = true;

    // GARDEN-211-FIX: Only mark router as started AFTER initial auth validation.
    // This prevents auth subscription callbacks from triggering premature route
    // renders (which could briefly route to landing and hide HUD on first garden load).
    this.hasStarted = true;

    // GARDEN-164: Handle post-auth redirect set by OAuth callback
    const postAuthRedirect = localStorage.getItem('garden_post_auth_redirect');
    if (postAuthRedirect) {
      localStorage.removeItem('garden_post_auth_redirect');
      // GARDEN-164-FIX: Explicitly navigate to servers route and return
      // Setting hash alone doesn't guarantee render; navigateTo ensures UI mounts
      window.location.hash = postAuthRedirect;
      this.navigateTo({ type: 'servers' }, false);
      return;
    }

    // Parse initial route
    const initialRoute = this.parseRoute(window.location.hash || '#/');
    this.navigateTo(initialRoute, false);
  }

  /** GARDEN-061: Show reconnect button when disconnected */
  private showReconnectButton(): void {
    // If button exists but is in "connecting" state from a previous attempt, reset it
    if (this.reconnectBtn) {
      this.reconnectBtn.textContent = 'Reconnect';
      this.reconnectBtn.disabled = false;
      return;
    }

    const btn = document.createElement('button');
    btn.textContent = 'Reconnect';
    btn.id = 'reconnect-btn';
    btn.className = 'reconnect-button';
    btn.onclick = () => {
      if (this.client) {
        connectionStatus.connecting();
        this.client.reconnect();
        btn.textContent = 'Connecting...';
        btn.disabled = true;
      }
    };

    document.body.appendChild(btn);
    this.reconnectBtn = btn;
  }

  /** GARDEN-061: Hide reconnect button when connected or reconnecting */
  private hideReconnectButton(): void {
    if (!this.reconnectBtn) return;
    this.reconnectBtn.remove();
    this.reconnectBtn = null;
  }

  /**
   * GARDEN-210: Dispose of the router and clean up all resources.
   * Call this when shutting down the app to prevent memory leaks.
   */
  destroy(): void {
    // Remove global event listeners
    if (this.boundHashChangeHandler) {
      window.removeEventListener('hashchange', this.boundHashChangeHandler);
      this.boundHashChangeHandler = null;
    }
    if (this.boundPopStateHandler) {
      window.removeEventListener('popstate', this.boundPopStateHandler);
      this.boundPopStateHandler = null;
    }

    // Unsubscribe from services
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
      this.unsubscribeAuth = null;
    }
    if (this.unsubscribeConnectionStatus) {
      this.unsubscribeConnectionStatus();
      this.unsubscribeConnectionStatus = null;
    }

    // Clean up current route resources
    this.cleanupGardenRouteResources();
    this.gardenShell?.destroy();
    this.gardenShell = null;

    // Clean up renderer and client
    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }
    if (this.client) {
      this.client.destroy();
      this.client = null;
    }
    if (this.loginUI) {
      this.loginUI.destroy?.();
      this.loginUI = null;
    }
    if (this.serverSelect) {
      this.serverSelect.destroy();
      this.serverSelect = null;
    }

    this.socialUIManager?.destroy();

    // Clean up toasts and buttons
    destroyToastContainer();
    this.hideReconnectButton();
    removeScreenshotButton();
    destroyOnboarding();

    // Reset connection state
    connectionStatus.reset();

    // Clear any pending timers or routes
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
